import { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Grid,
  Card,
  CardMedia,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Typography,
  Alert,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Paper,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Fullscreen as FullscreenIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/AuthStore';
import AssetRetrievalService from '../services/assetRetrievalService';

// Media agent types that generate images/videos
const MEDIA_AGENT_TYPES = [
  'ImageGeneration',
  'VideoGeneration',
  'VirtualTryOn',
  'FashionMultiAgent',
  'FashionDesignMultiAgent',
  'ImagePromptIterator',
  'VideoPromptIterator',
  'AnimationStudio',
  'ComicBookCreation',
];

// Asset type enum mapping (from backend)
enum AssetTypeEnum {
  Image = 0,
  Video = 1,
  Audio = 2,
  Text = 3,
}

// Status enum mapping (from backend)
enum AssetStatusEnum {
  Pending = 0,
  Processing = 1,
  Completed = 2,
  Failed = 3,
}

interface MediaAsset {
  id: string;
  assetType: number | 'Image' | 'Video'; // API returns number, we'll normalize to string
  agentType: string;
  subType?: string;
  assetUrl: string;
  model?: string;
  provider?: string;
  promptUsed?: string;
  sessionId?: string;
  status: number | string; // API returns number enum
  createdAt: string;
  completedAt?: string;
  metadataJson?: string;
}

// Helper function to normalize asset type from API response
const normalizeAssetType = (assetType: number | string): 'Image' | 'Video' | 'Unknown' => {
  if (typeof assetType === 'string') return assetType as 'Image' | 'Video';

  // assetType is a number enum from API
  switch (assetType) {
    case 0: // AssetTypeEnum.Image
      return 'Image';
    case 1: // AssetTypeEnum.Video
      return 'Video';
    default:
      return 'Unknown' as any;
  }
};

// Helper function to normalize status from API response
const normalizeStatus = (status: number | string): string => {
  if (typeof status === 'string') return status;

  // status is a number enum from API
  switch (status) {
    case 0: // AssetStatusEnum.Pending
      return 'Pending';
    case 1: // AssetStatusEnum.Processing
      return 'Processing';
    case 2: // AssetStatusEnum.Completed
      return 'Completed';
    case 3: // AssetStatusEnum.Failed
      return 'Failed';
    default:
      return 'Unknown';
  }
};

interface SearchResponse {
  userId: string;
  search: {
    prompt?: string;
    agentTypes: string[];
    assetTypes: string[];
  };
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
  };
  sorting: {
    sortBy: string;
    sortOrder: string;
  };
  assets: MediaAsset[];
  summary: {
    totalMediaAssets: number;
    byAgentType: Array<{ agentType: string; count: number }>;
    byAssetType: Array<{ assetType: string; count: number }>;
  };
}

const MediaGallery: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuthStore();

  // State
  const [tabValue, setTabValue] = useState(0); // 0: All, 1: Images, 2: Videos
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgentTypes, setSelectedAgentTypes] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'createdAt' | 'completedAt'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [selectedAsset, setSelectedAsset] = useState<MediaAsset | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(false);
  const [videoThumbnails, setVideoThumbnails] = useState<Map<string, string>>(new Map());

  // Fetch media assets using the new search API
  useEffect(() => {
    const fetchAssets = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Determine asset types based on tab
        let assetTypes: string[] | undefined;
        if (tabValue === 1) assetTypes = ['Image'];
        else if (tabValue === 2) assetTypes = ['Video'];

        const searchResponse = await AssetRetrievalService.searchMediaAssets(user.id, {
          searchPrompt: searchQuery.trim() || undefined,
          agentTypes: selectedAgentTypes.length > 0 ? selectedAgentTypes : undefined,
          assetTypes,
          page,
          pageSize,
          sortBy,
          sortOrder,
        });

        // Normalize assets to handle numeric enums from API
        if (searchResponse && searchResponse.assets) {
          searchResponse.assets = searchResponse.assets.map((asset: MediaAsset) => ({
            ...asset,
            assetType: normalizeAssetType(asset.assetType),
            status: normalizeStatus(asset.status),
          }));
        }

        setResponse(searchResponse);
      } catch (err) {
        console.error('[MediaGallery] Failed to fetch assets:', err);
        setError('Failed to load media assets');
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
  }, [user?.id, searchQuery, selectedAgentTypes, tabValue, page, sortBy, sortOrder]);

  const handleOpenViewer = (asset: MediaAsset) => {
    setSelectedAsset(asset);
    setViewerOpen(true);
    setPlayingVideo(false);
    setCopiedPrompt(false);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedAsset(null);
    setPlayingVideo(false);
    setCopiedPrompt(false);
  };

  const handleCopyPrompt = async (prompt: string) => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 2000);
    } catch (err) {
      console.error('Failed to copy prompt:', err);
    }
  };

  const handleTabChange = (_: any, newValue: number) => {
    setTabValue(newValue);
    setPage(1); // Reset to first page when changing tabs
  };

  const handleAgentTypeToggle = (agentType: string) => {
    setSelectedAgentTypes(prev => {
      if (prev.includes(agentType)) {
        return prev.filter(t => t !== agentType);
      }
      return [...prev, agentType];
    });
    setPage(1); // Reset to first page when filtering
  };

  const handleClearFilters = () => {
    setSelectedAgentTypes([]);
    setSearchQuery('');
    setPage(1);
  };

  // Generate thumbnail from video's first frame
  const generateVideoThumbnail = (videoUrl: string, assetId: string) => {
    // Don't regenerate if already exists
    if (videoThumbnails.has(assetId)) return;

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = videoUrl;
    video.currentTime = 0.1; // Seek to 0.1s to ensure frame is loaded

    video.onloadeddata = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setVideoThumbnails(prev => new Map(prev).set(assetId, thumbnailDataUrl));
      }
    };
  };

  const renderAssetCard = (asset: MediaAsset) => {
    // Only VideoGeneration agent type produces actual videos, everything else is an image
    const isVideo = asset.agentType === 'VideoGeneration';
    const thumbnailUrl = isVideo ? videoThumbnails.get(asset.id) : null;

    // Generate thumbnail for videos on mount
    if (isVideo && !thumbnailUrl) {
      generateVideoThumbnail(asset.assetUrl, asset.id);
    }

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={asset.id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            position: 'relative',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4,
              '& .hover-overlay': {
                opacity: 1,
              },
            },
          }}
          onClick={() => handleOpenViewer(asset)}
        >
          {/* Media Preview */}
          <Box sx={{ position: 'relative', paddingTop: '100%', bgcolor: 'grey.900' }}>
            {isVideo ? (
              <>
                {thumbnailUrl ? (
                  <CardMedia
                    component="img"
                    image={thumbnailUrl}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.800',
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}
              </>
            ) : (
              <CardMedia
                component="img"
                image={asset.assetUrl}
                src={asset.assetUrl}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}

            {/* Hover Overlay */}
            <Box
              className="hover-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                opacity: 0,
                transition: 'opacity 0.3s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
              }}
            >
              <IconButton
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  },
                  mb: 1,
                }}
              >
                <FullscreenIcon fontSize="large" />
              </IconButton>
              <Typography variant="caption" align="center" sx={{ mt: 1 }}>
                {asset.agentType}
              </Typography>
              {asset.subType && (
                <Typography variant="caption" align="center">
                  {asset.subType}
                </Typography>
              )}
              {asset.model && (
                <Typography variant="caption" align="center" sx={{ opacity: 0.8 }}>
                  {asset.model}
                </Typography>
              )}
            </Box>

            {/* Video Play Icon */}
            {isVideo && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PlayIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
            )}
          </Box>
        </Card>
      </Grid>
    );
  };

  if (loading && !response) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const assets = response?.assets || [];
  const pagination = response?.pagination;
  const summary = response?.summary;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
        <Typography
          variant="h4"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 0.5, sm: 1 },
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
            flexWrap: 'wrap'
          }}
        >
          <ImageIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          Media Gallery
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 0.5, sm: 0 }
          }}
        >
          Browse and search all your generated images and videos across all agents
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by prompt, model, agent type..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1); // Reset to first page on search
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Filters and Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={2}>
          {/* Agent Type Filters */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FilterIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Agents:
            </Typography>
            {MEDIA_AGENT_TYPES.map((agentType) => (
              <Chip
                key={agentType}
                label={agentType}
                size="small"
                onClick={() => handleAgentTypeToggle(agentType)}
                color={selectedAgentTypes.includes(agentType) ? 'primary' : 'default'}
                variant={selectedAgentTypes.includes(agentType) ? 'filled' : 'outlined'}
              />
            ))}
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Sort Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SortIcon fontSize="small" color="action" />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value as 'createdAt' | 'completedAt')}
              >
                <MenuItem value="createdAt">Created</MenuItem>
                <MenuItem value="completedAt">Completed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Order</InputLabel>
              <Select
                value={sortOrder}
                label="Order"
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              >
                <MenuItem value="desc">Newest</MenuItem>
                <MenuItem value="asc">Oldest</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Clear Filters Button */}
          {(selectedAgentTypes.length > 0 || searchQuery) && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          )}
        </Stack>

        {/* Summary Statistics */}
        {summary && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                Total: <strong>{summary.totalMediaAssets}</strong> assets
              </Typography>
              {summary.byAssetType.map((stat) => (
                <Typography key={stat.assetType} variant="caption" color="text.secondary">
                  {stat.assetType}s: <strong>{stat.count}</strong>
                </Typography>
              ))}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? 'fullWidth' : 'standard'}
        >
          <Tab label="All Media" icon={<ImageIcon />} iconPosition="start" />
          <Tab label="Images" icon={<ImageIcon />} iconPosition="start" />
          <Tab label="Videos" icon={<VideoIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Gallery Grid */}
      {!loading && (
        <>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {assets.map(renderAssetCard)}
          </Grid>

          {assets.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No media assets found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchQuery || selectedAgentTypes.length > 0
                  ? 'Try adjusting your search or filters'
                  : 'Generate some images or videos to get started'}
              </Typography>
            </Box>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Asset Viewer Dialog */}
      <Dialog
        open={viewerOpen}
        onClose={handleCloseViewer}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        {selectedAsset && (
          <>
            <DialogContent
              sx={{
                p: 0,
                bgcolor: 'black',
                position: 'relative',
              }}
            >
              {/* Close Button */}
              <IconButton
                onClick={handleCloseViewer}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  zIndex: 1,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.8)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Media Display */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '70vh',
                  position: 'relative',
                }}
              >
                {/* Only VideoGeneration agent type produces actual videos */}
                {selectedAsset.agentType === 'VideoGeneration' ? (
                  <>
                    {/* Video Thumbnail (shown before play) */}
                    {!playingVideo && (
                      <Box
                        sx={{
                          position: 'absolute',
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                        onClick={() => setPlayingVideo(true)}
                      >
                        {videoThumbnails.get(selectedAsset.id) ? (
                          <img
                            src={videoThumbnails.get(selectedAsset.id)}
                            alt="Video thumbnail"
                            style={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: '90vh',
                              objectFit: 'contain',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '50vh',
                            }}
                          >
                            <CircularProgress sx={{ color: 'white' }} />
                          </Box>
                        )}
                        <IconButton
                          sx={{
                            position: 'absolute',
                            color: 'white',
                            bgcolor: 'rgba(0, 0, 0, 0.6)',
                            width: 80,
                            height: 80,
                            '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.8)',
                            },
                          }}
                        >
                          <PlayIcon sx={{ fontSize: 48 }} />
                        </IconButton>
                      </Box>
                    )}
                    {/* Actual Video Player */}
                    {playingVideo && (
                      <video
                        src={selectedAsset.assetUrl}
                        controls
                        autoPlay
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: '90vh',
                        }}
                      />
                    )}
                  </>
                ) : (
                  // Fallback: treat everything else as an image (including Video assets with other subtypes)
                  <img
                    src={selectedAsset.assetUrl}
                    alt="Asset"
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '90vh',
                      objectFit: 'contain',
                    }}
                  />
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ bgcolor: 'grey.900', color: 'white', px: 3, py: 2, flexDirection: 'column', alignItems: 'flex-start' }}>
              <Box sx={{ width: '100%', mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'grey.300', mb: 0.5 }}>
                  {selectedAsset.agentType}
                  {selectedAsset.subType && ` • ${selectedAsset.subType}`}
                  {selectedAsset.provider && ` • ${selectedAsset.provider}`}
                </Typography>
                {selectedAsset.promptUsed && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'grey.400', flex: 1 }}>
                      Prompt: {selectedAsset.promptUsed.length > 100
                        ? `${selectedAsset.promptUsed.substring(0, 100)}...`
                        : selectedAsset.promptUsed}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyPrompt(selectedAsset.promptUsed!)}
                      sx={{
                        color: copiedPrompt ? 'success.main' : 'grey.400',
                        p: 0.5,
                        '&:hover': {
                          color: 'white',
                        },
                      }}
                      title="Copy full prompt to clipboard"
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                {copiedPrompt && (
                  <Typography variant="caption" sx={{ color: 'success.main', display: 'block', mb: 0.5 }}>
                    Prompt copied to clipboard!
                  </Typography>
                )}
                <Typography variant="caption" display="block" sx={{ color: 'grey.500' }}>
                  {new Date(selectedAsset.createdAt).toLocaleString()}
                  {selectedAsset.model && ` • Model: ${selectedAsset.model}`}
                  {selectedAsset.sessionId && ` • Session: ${selectedAsset.sessionId.substring(0, 8)}...`}
                </Typography>
              </Box>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={handleCloseViewer} sx={{ color: 'white' }}>
                  Close
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default MediaGallery;
